import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Layout, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMediaPredicate } from 'react-media-hook';
import { PageHeader } from 'common/components/Page/PageHeader';
import { Loader } from 'common/components/Loader';
import { SearchInput } from 'common/components/Input/SearchInput';
import { PageContent } from 'common/components/Page/PageContent';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { PageHeaderMob } from 'common/components/Page/PageHeaderMob';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { ERoutesFranchiseePrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { history } from 'app/store';
import { getPurchaseOrderCollection, getPurchaseOrderStoreCollection } from 'app/store/reducers/purchaseOrder.reducer';
import { TrackPOKanbanBoard } from 'entities/PurchaseOrder/components/TrackPOKanbanBoard';

export const TrackPOPage: FC = () => {
  const dispatch = useAppDispatch();

  const { purchaseOrderCollection, purchaseOrderCollectionLoading, purchaseOrderStoreCollection } = useAppSelector(
    (state) => state.purchaseOrder
  );

  const [storeCode, setStoreCode] = useState<string | number | undefined>();
  const [search, setSearch] = useState<string>('');
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  useEffect(() => {
    dispatch(getPurchaseOrderStoreCollection());
  }, []);

  // MBC-84: storeCode === undefined means "All stores" — fetch aggregate scoped to the user's email.
  // sonum filters by SO#; BE requires min 3 chars — shorter input is not sent (no filter).
  useEffect(() => {
    dispatch(
      getPurchaseOrderCollection({
        storeCode: storeCode as string | undefined,
        sonum: search.length >= 3 ? search : undefined,
        limit: 0,
      })
    );
  }, [storeCode, search]);

  const orderCount = purchaseOrderCollection?.meta.count || 0;

  const changeStore = (value?: string | number) => {
    setStoreCode(value);
  };

  return (
    <Layout className="relative height-full flex flex__column">
      {purchaseOrderCollectionLoading && <Loader />}

      {isMobile ? (
        <PageHeaderMob
          title={
            <div>
              <div className="mb-3 flex flex-align-center">
                {sidebarMenuIcons[ESidebarMenuKey.TrackPO]}
                <span className="ml-3 ff-regular">{ESidebarMenuLabel[ESidebarMenuKey.TrackPO]}</span>
              </div>
              <span className="ff-regular fs-xxs color-gray-1">{`${orderCount} total`}</span>
            </div>
          }
          extra={[
            <PageStoreSelector
              key="storeSelector"
              stores={purchaseOrderStoreCollection?.data}
              onChange={changeStore}
              allowAll
            />,
          ]}
        />
      ) : (
        <PageHeader
          title={
            <div>
              <span>Track PO Real-Time</span>
              <span className="ml-3 ff-regular fs-xxs color-gray-1">{`${orderCount} total`}</span>
            </div>
          }
          extra={
            <Row gutter={[8, 8]}>
              <Col style={{ width: 300 }}>
                <SearchInput placeholder="Search by SO#" value={search} onChange={setSearch} />
              </Col>
              <Col>
                <PageStoreSelector stores={purchaseOrderStoreCollection?.data} onChange={changeStore} allowAll />
              </Col>
              <Col>
                <Button
                  className="btn-primary"
                  icon={<PlusOutlined />}
                  onClick={() => history.push(`${RoutesCirPrefix}${ERoutesFranchiseePrivate.GeneratePO}`)}
                >
                  New Order
                </Button>
              </Col>
            </Row>
          }
        />
      )}

      <PageContent>
        <div
          className={`page-content__container page-content__scroll-container ${
            isMobile ? 'scrollbar-none' : 'scrollbar-x'
          }`}
        >
          <TrackPOKanbanBoard />
        </div>
      </PageContent>
    </Layout>
  );
};
